import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertCircle, ArrowLeft, Download, FileText, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/features/LoadingSpinner'
import { PDFViewer } from '@/components/features/PDFViewer'
import { ApiError } from '@/lib/api/client'
import { createProjectInvoice, getProjectInvoicePdfUrl, previewProjectInvoicePdf } from '@/lib/api/projects'
import { invoiceErrorMessage } from '@/lib/invoiceErrors'
import { ImportedLanguageWarning } from '@/components/features/imported-language/ImportedLanguageWarning'
import { getFinalInvoiceLanguageCheck } from '@/components/features/imported-language/imported-language'
import type { CreateInvoiceLineDto } from '@/types'

interface StandaloneLocationState {
  invoiceType: 'standalone' | 'direct_sale' | 'credit_note'
  invoiceDate: string
  lines: CreateInvoiceLineDto[]
  referencedInvoiceIds?: string[]
  // SOLY-CN-002 — Mode A/B preview support. Mode A passes no lines (BE derives
  // from the referenced invoice). Mode B passes partialAmountTtc OR partialProportion.
  mode?: 'full_cancellation' | 'partial_proportional' | 'free_lines'
  partialAmountTtc?: number
  partialProportion?: number
  // FE-only fields the preview page doesn't use for API calls but round-trips
  // back to CreditNotePage so the form rehydrates the user's raw inputs.
  partialAmount?: string
  partialInputMode?: 'ttc' | 'percent'
  // SOLY-CN-003 — optional free-text credit note reason. Forwarded to both the
  // preview and the persisted emission so the PDF matches.
  reason?: string
}

export default function InvoicePreviewPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  // Standalone passes data via location state (lines can't go in URL)
  const locationState = location.state as StandaloneLocationState | null
  const isStandalone = locationState?.invoiceType === 'standalone' || locationState?.invoiceType === 'direct_sale' || locationState?.invoiceType === 'credit_note'

  const invoiceType = isStandalone
    ? (locationState?.invoiceType ?? 'standalone')
    : ((searchParams.get('invoice_type') as 'deposit' | 'final' | null) || 'deposit')

  // Backend only knows 'standalone' — map 'direct_sale' before API calls
  const apiInvoiceType = invoiceType === 'direct_sale' ? 'standalone' : invoiceType
  const invoiceDate = isStandalone
    ? (locationState?.invoiceDate ?? '')
    : (searchParams.get('invoice_date') || '')
  const amountRaw = searchParams.get('amount')
  const amountWithTax = amountRaw ? Number(amountRaw) : undefined
  const standaloneLines = useMemo(
    () => (isStandalone ? (locationState?.lines ?? []) : undefined),
    // locationState is stable for the lifetime of this page render (comes from navigation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isStandalone]
  )

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)

  const [isGenerating, setIsGenerating] = useState(false)

  const referencedInvoiceIds = locationState?.referencedInvoiceIds
  const creditNoteMode = locationState?.mode
  const partialAmountTtc = locationState?.partialAmountTtc
  const partialProportion = locationState?.partialProportion
  const partialAmountRaw = locationState?.partialAmount
  const partialInputModeRaw = locationState?.partialInputMode
  const creditNoteReason = locationState?.reason

  const title = useMemo(() => {
    if (invoiceType === 'deposit') return "Aperçu de la facture d'acompte"
    if (invoiceType === 'standalone') return 'Aperçu de la facture supplémentaire'
    if (invoiceType === 'direct_sale') return 'Aperçu de la facture directe'
    if (invoiceType === 'credit_note') return 'Aperçu de la note de crédit'
    return 'Aperçu de la facture finale'
  }, [invoiceType])

  useEffect(() => {
    if (!projectId) {
      navigate(`/projects/${projectId}?tab=invoices`)
      return
    }

    // Mode A/B credit notes carry no lines (BE derives from referenced invoice).
    // Skip the lines-presence guard for them; require referencedInvoiceIds instead.
    const isCreditNoteRefBased =
      invoiceType === 'credit_note' &&
      (creditNoteMode === 'full_cancellation' || creditNoteMode === 'partial_proportional')
    if (isStandalone && !isCreditNoteRefBased && !standaloneLines?.length) {
      navigate(invoiceType === 'direct_sale'
        ? `/projets/${projectId}/invoices/direct`
        : invoiceType === 'credit_note'
          ? `/projets/${projectId}/invoices/credit-note`
          : `/projets/${projectId}/invoices/standalone`)
      return
    }
    if (isCreditNoteRefBased && !referencedInvoiceIds?.length) {
      navigate(`/projets/${projectId}/invoices/credit-note`)
      return
    }

    if (!invoiceDate) {
      navigate(`/projets/${projectId}/invoices/new?step=2&invoice_type=${invoiceType}`)
      return
    }

    if (invoiceType === 'deposit' && (!amountWithTax || !Number.isFinite(amountWithTax) || amountWithTax < 1)) {
      navigate(`/projets/${projectId}/invoices/new?step=2&invoice_type=${invoiceType}`)
      return
    }

    let isMounted = true
    let urlToRevoke: string | null = null

    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const blob = await previewProjectInvoicePdf(projectId, {
          invoiceType: apiInvoiceType,
          amountWithTax: apiInvoiceType === 'deposit' ? amountWithTax : undefined,
          invoiceDate,
          lines: isStandalone && !isCreditNoteRefBased ? standaloneLines : undefined,
          ...(referencedInvoiceIds?.length ? { referencedInvoiceIds } : {}),
          ...(creditNoteMode ? { mode: creditNoteMode } : {}),
          ...(partialAmountTtc !== undefined ? { partialAmountTtc } : {}),
          ...(partialProportion !== undefined ? { partialProportion } : {}),
          ...(creditNoteReason ? { reason: creditNoteReason } : {}),
        })

        if (!isMounted) return

        setPdfBlob(blob)
        urlToRevoke = URL.createObjectURL(blob)
        setPdfObjectUrl(urlToRevoke)
      } catch (e) {
        if (!isMounted) return
        setError(invoiceErrorMessage(e, "Impossible de charger l'aperçu PDF"))
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    load()

    return () => {
      isMounted = false
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke)
    }
  }, [projectId, invoiceType, invoiceDate, amountWithTax, isStandalone, standaloneLines, navigate, creditNoteMode, partialAmountTtc, partialProportion, referencedInvoiceIds, creditNoteReason])

  const handleBack = () => {
    navigate(`/projects/${projectId}?tab=invoices`)
  }

  const handleBackToForm = () => {
    if (invoiceType === 'credit_note') {
      navigate(`/projets/${projectId}/invoices/credit-note`, {
        state: {
          invoiceDate,
          lines: standaloneLines,
          referencedInvoiceIds,
          // SOLY-CN-002 — round-trip mode + partial inputs so the form
          // rehydrates the user's selection instead of resetting to defaults.
          mode: creditNoteMode,
          partialAmountTtc,
          partialProportion,
          partialAmount: partialAmountRaw,
          partialInputMode: partialInputModeRaw,
          // SOLY-CN-003 — round-trip the reason so the form rehydrates it on Retour.
          reason: creditNoteReason,
        },
      })
      return
    }
    if (invoiceType === 'direct_sale') {
      navigate(`/projets/${projectId}/invoices/direct`, {
        state: { invoiceDate, lines: standaloneLines },
      })
      return
    }
    if (invoiceType === 'standalone') {
      navigate(`/projets/${projectId}/invoices/standalone`, {
        state: { invoiceDate, lines: standaloneLines },
      })
      return
    }
    const params = new URLSearchParams()
    params.set('step', '2')
    params.set('invoice_type', invoiceType)
    navigate(`/projets/${projectId}/invoices/new?${params.toString()}`)
  }

  const handleDownloadPreview = () => {
    if (!pdfBlob) return

    const url = URL.createObjectURL(pdfBlob)
    const a = document.createElement('a')
    a.href = url
    const filename = invoiceType === 'deposit'
      ? 'Apercu-Facture-Acompte.pdf'
      : invoiceType === 'direct_sale'
        ? 'Apercu-Facture-Directe.pdf'
        : invoiceType === 'standalone'
          ? 'Apercu-Facture-Supplementaire.pdf'
          : invoiceType === 'credit_note'
            ? 'Apercu-Note-Credit.pdf'
            : 'Apercu-Facture-Finale.pdf'
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleGenerate = async () => {
    if (!projectId) return
    if (!invoiceDate) return
    if (invoiceType === 'deposit' && (!amountWithTax || !Number.isFinite(amountWithTax) || amountWithTax < 1)) return
    const isCreditNoteRefBased =
      invoiceType === 'credit_note' &&
      (creditNoteMode === 'full_cancellation' || creditNoteMode === 'partial_proportional')
    if (isStandalone && !isCreditNoteRefBased && !standaloneLines?.length) return
    if (isCreditNoteRefBased && !referencedInvoiceIds?.length) return

    try {
      setIsGenerating(true)

      const created = await createProjectInvoice(projectId, {
        invoiceType: apiInvoiceType,
        amountWithTax: apiInvoiceType === 'deposit' ? amountWithTax : undefined,
        invoiceDate,
        lines: isStandalone && !isCreditNoteRefBased ? standaloneLines : undefined,
        ...(referencedInvoiceIds?.length ? { referencedInvoiceIds } : {}),
        ...(creditNoteMode ? { mode: creditNoteMode } : {}),
        ...(partialAmountTtc !== undefined ? { partialAmountTtc } : {}),
        ...(partialProportion !== undefined ? { partialProportion } : {}),
        ...(creditNoteReason ? { reason: creditNoteReason } : {}),
      })

      try {
        const pdf = await getProjectInvoicePdfUrl(projectId, created.id)
        if (pdf.url) {
          window.open(pdf.url, '_blank', 'noopener,noreferrer')
        }
      } catch {
        // ignore
      }

      // Clear draft from sessionStorage after successful generation
      try {
        sessionStorage.removeItem(`invoice-draft:${projectId}:${invoiceType}`)
      } catch { /* ignore */ }

      toast.success(invoiceType === 'credit_note' ? 'Note de crédit générée' : 'Facture générée')
      navigate(`/projects/${projectId}?tab=invoices`)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SALE_CANCELLED') {
        // GAP-B2 — the sale was cancelled (possibly from another tab) between page load and POST.
        toast.error('La vente de ce projet est annulée — facturation impossible')
        navigate(`/projects/${projectId}?tab=invoices`)
      } else {
        toast.error(invoiceErrorMessage(e, invoiceType === 'credit_note' ? 'Erreur lors de la génération de la note de crédit' : 'Erreur lors de la génération de la facture'))
      }
    } finally {
      setIsGenerating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !pdfObjectUrl) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-4">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">Erreur de chargement</h2>
            <p className="text-sm text-muted-foreground">{error || 'Aperçu non disponible'}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleBackToForm} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Modifier
          </Button>
          <Button onClick={() => window.location.reload()}>Réessayer</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-muted/30">
      <div className="sticky top-0 z-50 bg-background border-b shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={handleBackToForm} disabled={isGenerating}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <div className="border-l pl-3">
              <h1 className="text-lg font-semibold">{title}</h1>
              <p className="text-xs text-muted-foreground">Aperçu non contractuel</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPreview}
              disabled={isGenerating}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Télécharger
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="h-full grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 p-4">
          <div className="h-full overflow-hidden bg-gray-100 rounded-lg p-2">
            <div className="h-full w-full bg-white shadow-2xl rounded-lg overflow-hidden">
              <PDFViewer pdfSource={pdfObjectUrl} title="Invoice Preview" />
            </div>
          </div>

          <div className="bg-background border rounded-lg p-4 h-fit max-h-full overflow-y-auto">
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium">Actions</div>
                <div className="text-xs text-muted-foreground">Vérifie l’aperçu avant de générer le document.</div>
              </div>

              <div className="space-y-1 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">
                    {invoiceType === 'deposit' ? 'Acompte'
                      : invoiceType === 'direct_sale' ? 'Facture directe'
                      : invoiceType === 'standalone' ? 'Supplémentaire'
                      : invoiceType === 'credit_note' ? 'Note de crédit'
                      : 'Finale'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">{invoiceDate}</span>
                </div>
                {invoiceType === 'deposit' ? (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Montant</span>
                    <span className="font-medium">{(amountWithTax || 0).toFixed(2)} €</span>
                  </div>
                ) : null}
              </div>

              {invoiceType === 'final' && projectId ? (
                <ImportedLanguageWarning load={() => getFinalInvoiceLanguageCheck(projectId)} />
              ) : null}

              <Button onClick={handleGenerate} disabled={isGenerating} size="lg" className="w-full gap-2">
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Génération…
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    {invoiceType === 'credit_note' ? 'Générer la note de crédit' : 'Générer la facture'}
                  </>
                )}
              </Button>

              <Button onClick={handleBack} variant="outline" className="w-full" disabled={isGenerating}>
                Retour au projet
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
